import React, { useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import styled from '@emotion/styled';
import { Button, Tooltip, Dropdown, Checkbox, Spin } from 'antd';
import type { MenuProps } from 'antd';
import {
  BarChartOutlined,
  UpOutlined,
  DownOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useRecoilValue } from 'recoil';
import { uiThemeAtom } from 'packages/at-shell/state/recoil/ui';
import {
  allSearchResultsAtom,
  filteredSearchResultsListAtom,
} from 'packages/omnitag/state/text-search';
import { selectedResultsAtom } from 'packages/omnitag/state/selected-results';
import { OmnitagResultItem } from 'packages/lib/api/omnitag/contract/omnitag-search';
import {
  ALL_CHARTS,
  computeDistribution,
  DEFAULT_FIELDS,
  MetadataField,
} from 'packages/omnitag/results/grid-stats-distribution';

const PanelContainer = styled.div<{ isDark: boolean; isExpanded: boolean }>`
  border-top: 1px solid ${(p): string => (p.isDark ? '#303030' : '#e8e8e8')};
  background: ${(p): string => (p.isDark ? '#1f1f1f' : '#fafafa')};
  transition: height 0.2s ease-out;
  height: ${(p): string => (p.isExpanded ? '180px' : '36px')};
  overflow-x: auto;
  overflow-y: hidden;
  flex-shrink: 0;
`;

const PanelHeader = styled.div<{ isDark: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  height: 36px;
  border-bottom: 1px solid ${(p): string => (p.isDark ? '#303030' : '#e8e8e8')};
  cursor: pointer;
  user-select: none;

  &:hover {
    background: ${(p): string => (p.isDark ? '#262626' : '#f0f0f0')};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModeToggle = styled.span<{
  isDark: boolean;
  active: boolean;
  disabled?: boolean;
}>`
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  cursor: ${(p): string => (p.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(p): number => (p.disabled ? 0.4 : 1)};
  background: ${(p): string =>
    p.active ? (p.isDark ? '#177ddc' : '#1890ff') : 'transparent'};
  color: ${(p): string => (p.active ? '#fff' : p.isDark ? '#a0a0a0' : '#666')};

  &:hover {
    background: ${(p): string =>
      p.disabled
        ? 'transparent'
        : p.active
        ? p.isDark
          ? '#177ddc'
          : '#1890ff'
        : p.isDark
        ? '#303030'
        : '#e8e8e8'};
  }
`;

const ChartsContainer = styled.div`
  display: flex;
  gap: 16px;
  padding: 12px 16px;
  overflow-x: auto;
  height: calc(100% - 36px);
`;

const ChartCard = styled.div`
  min-width: 200px;
  max-width: 260px;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ChartTitle = styled.div<{ isDark: boolean }>`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(p): string => (p.isDark ? '#a0a0a0' : '#666')};
  margin-bottom: 8px;
`;

const BarContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
  overflow-y: auto;
`;

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 16px;
`;

const BarLabel = styled.div<{ isDark: boolean }>`
  font-size: 10px;
  width: 58px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(p): string => (p.isDark ? '#d0d0d0' : '#333')};
  flex-shrink: 0;
`;

const BarTrack = styled.div<{ isDark: boolean }>`
  flex: 1;
  height: 10px;
  background: ${(p): string => (p.isDark ? '#303030' : '#e8e8e8')};
  border-radius: 2px;
  overflow: hidden;
`;

const BarFill = styled.div<{ width: number; color: string }>`
  height: 100%;
  width: ${(p): number => p.width}%;
  background: ${(p): string => p.color};
  border-radius: 2px;
  transition: width 0.2s ease-out;
`;

const BarCount = styled.div<{ isDark: boolean }>`
  font-size: 10px;
  width: 72px;
  text-align: right;
  color: ${(p): string => (p.isDark ? '#808080' : '#999')};
  flex-shrink: 0;
  white-space: nowrap;
`;

const OverflowIndicator = styled.div<{ isDark: boolean }>`
  font-size: 9px;
  color: ${(p): string => (p.isDark ? '#606060' : '#999')};
  text-align: center;
  margin-top: 2px;
`;

const FieldSelectorMenu = styled.div<{ isDark: boolean }>`
  padding: 8px;
  background: ${(p): string => (p.isDark ? '#1f1f1f' : '#fff')};
  border-radius: 6px;
  min-width: 160px;
`;

const FieldSelectorTitle = styled.div<{ isDark: boolean }>`
  font-size: 11px;
  font-weight: 600;
  color: ${(p): string => (p.isDark ? '#a0a0a0' : '#666')};
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FieldOption = styled.div`
  padding: 4px 0;
`;

const CHART_COLORS = [
  '#1890ff',
  '#52c41a',
  '#faad14',
  '#eb2f96',
  '#722ed1',
  '#13c2c2',
];

type StatsMode = 'all' | 'selected';

export const GridStatsPanel: React.FC = () => {
  const theme = useRecoilValue(uiThemeAtom);
  const isDark = theme === 'dark';

  const [isExpanded, setIsExpanded] = useState(false);
  const [mode, setMode] = useState<StatsMode>('all');
  const [selectedFields, setSelectedFields] =
    useState<Set<MetadataField>>(DEFAULT_FIELDS);

  const searchResults = useAtomValue(allSearchResultsAtom);
  const allItems = useAtomValue(filteredSearchResultsListAtom);
  const selectedResults = useAtomValue(selectedResultsAtom);

  const selectedItems = useMemo<OmnitagResultItem[]>(
    () => Array.from(selectedResults),
    [selectedResults]
  );

  const hasSelection = selectedItems.length > 0;

  // When all items are deselected, snap back to 'all' so the mode toggle
  // doesn't silently re-activate 'selected' the next time items are chosen.
  useEffect(() => {
    if (!hasSelection) setMode('all');
  }, [hasSelection]);

  const effectiveMode = mode === 'selected' && !hasSelection ? 'all' : mode;

  const activeItems = effectiveMode === 'selected' ? selectedItems : allItems;

  const activeCharts = useMemo(
    () => ALL_CHARTS.filter((c) => selectedFields.has(c.field)),
    [selectedFields]
  );

  const distributions = useMemo(
    () =>
      activeCharts.map((chart) => {
        const result = computeDistribution(activeItems, chart.field);
        return { ...chart, data: result.data, overflow: result.overflow };
      }),
    [activeItems, activeCharts]
  );

  // Hide entirely until the user has run at least one search
  if (!searchResults) return null;

  const toggleField = (field: MetadataField): void => {
    setSelectedFields((prev) => {
      const next = new Set(prev);
      if (next.has(field)) {
        if (next.size > 1) next.delete(field);
      } else {
        next.add(field);
      }
      return next;
    });
  };

  const toggleExpand = (): void => setIsExpanded((v) => !v);

  const handleModeClick = (newMode: StatsMode, e: React.MouseEvent): void => {
    e.stopPropagation();
    if (newMode === 'selected' && !hasSelection) return;
    setMode(newMode);
  };

  const fieldSelectorDropdown: MenuProps['items'] = [
    {
      key: 'selector',
      label: (
        <FieldSelectorMenu
          isDark={isDark}
          onClick={(e): void => e.stopPropagation()}
        >
          <FieldSelectorTitle isDark={isDark}>Show Fields</FieldSelectorTitle>
          {ALL_CHARTS.map((chart) => (
            <FieldOption key={chart.field}>
              <Checkbox
                checked={selectedFields.has(chart.field)}
                onChange={(): void => toggleField(chart.field)}
              >
                {chart.title}
              </Checkbox>
            </FieldOption>
          ))}
        </FieldSelectorMenu>
      ),
    },
  ];

  const isLoading = searchResults.isValidating;

  const headerLabel =
    effectiveMode === 'selected'
      ? `${selectedItems.length} selected`
      : `${allItems.length} images`;

  return (
    <PanelContainer isDark={isDark} isExpanded={isExpanded}>
      <PanelHeader isDark={isDark} onClick={toggleExpand}>
        <HeaderLeft>
          <BarChartOutlined />
          <span>
            Distribution ({isLoading ? <Spin size="small" /> : headerLabel})
          </span>
        </HeaderLeft>
        <HeaderRight>
          {isExpanded && (
            <>
              <ModeToggle
                isDark={isDark}
                active={effectiveMode === 'all'}
                onClick={(e): void => handleModeClick('all', e)}
              >
                All
              </ModeToggle>
              <Tooltip
                title={
                  hasSelection
                    ? ''
                    : 'Select images in the grid to compare distributions'
                }
                placement="top"
              >
                <ModeToggle
                  isDark={isDark}
                  active={effectiveMode === 'selected'}
                  disabled={!hasSelection}
                  onClick={(e): void => handleModeClick('selected', e)}
                >
                  Selected{hasSelection ? ` (${selectedItems.length})` : ''}
                </ModeToggle>
              </Tooltip>
              <Dropdown
                menu={{ items: fieldSelectorDropdown }}
                trigger={['click']}
                placement="topRight"
              >
                <Tooltip title="Select fields to display">
                  <Button
                    type="text"
                    size="small"
                    icon={<SettingOutlined />}
                    onClick={(e): void => e.stopPropagation()}
                  />
                </Tooltip>
              </Dropdown>
            </>
          )}
          <Tooltip title={isExpanded ? 'Collapse' : 'Expand distributions'}>
            <Button
              type="text"
              size="small"
              icon={isExpanded ? <DownOutlined /> : <UpOutlined />}
              onClick={(e): void => {
                e.stopPropagation();
                toggleExpand();
              }}
            />
          </Tooltip>
        </HeaderRight>
      </PanelHeader>

      {isExpanded && (
        <ChartsContainer>
          {distributions.map((chart) => {
            const maxCount = Math.max(...chart.data.map((d) => d.count), 1);
            const total = activeItems.length || 1;
            const color = CHART_COLORS[chart.colorIndex % CHART_COLORS.length];
            return (
              <ChartCard key={chart.field}>
                <ChartTitle isDark={isDark}>{chart.title}</ChartTitle>
                <BarContainer>
                  {chart.data.map((item) => {
                    const pct = Math.round((item.count / total) * 100);
                    return (
                      <Tooltip
                        key={item.label}
                        title={`${item.label}: ${item.count} (${pct}%)`}
                        placement="right"
                      >
                        <BarRow>
                          <BarLabel isDark={isDark}>{item.shortLabel}</BarLabel>
                          <BarTrack isDark={isDark}>
                            <BarFill
                              width={(item.count / maxCount) * 100}
                              color={color}
                            />
                          </BarTrack>
                          <BarCount isDark={isDark}>
                            {item.count} ({pct}%)
                          </BarCount>
                        </BarRow>
                      </Tooltip>
                    );
                  })}
                  {chart.overflow > 0 && (
                    <OverflowIndicator isDark={isDark}>
                      +{chart.overflow} more
                    </OverflowIndicator>
                  )}
                </BarContainer>
              </ChartCard>
            );
          })}
        </ChartsContainer>
      )}
    </PanelContainer>
  );
};
